import React, { useCallback, useEffect, useMemo, useState } from 'react';
import uuid from 'react-uuid';
import { useAppDispatch } from 'src/app/hooks';

import GridWrapper from 'src/components/GridWrapper';
import { fetchPOLineItemsData } from 'src/features/LineItemsGridSlice';
import { IPoLineItems } from 'src/types/PoLineItems';
import { modifyRequest } from 'src/utils/requestInterceptor';

import { getter, State } from '@progress/kendo-data-query';
import {
  getSelectedState,
  GridDataStateChangeEvent,
  GridPagerSettings,
  GridRowDoubleClickEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import usePoLineItemColumns from 'src/assets/static/new_receipt/usePoLineItemColumns';
import InvoiceGridToolbar from 'src/sections/enter_invoice/InvoiceGridToolBar';

interface ILineItemWizard {
  title: string;
  poId: number | undefined;
  isInvoice: boolean;
}
const DATA_ITEM_KEY: string = 'rowId';
const ItemIdGetter = getter(DATA_ITEM_KEY);
const GridDataState: State = {
  sort: [],
  skip: 0,
  take: 10,
};
const gridPagerSettings: GridPagerSettings = {
  buttonCount: 2,
  pageSizes: [10, 20, 50, 100],
};

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'single',
};
const defaultProps = {
  tableTextId: 'Line Items',
  DataItemKey: DATA_ITEM_KEY,
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

const LineItemWizard: React.FC<ILineItemWizard> = ({ title, poId, isInvoice }) => {
  const dispatch = useAppDispatch();
  const [dataState, setDataState] = useState(GridDataState);
  const [lineItemData, setLineItemData] = useState<IPoLineItems[]>([]);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const poLineItemColumns = usePoLineItemColumns(isInvoice);
  const [selectedRow, setSelectedRow] = useState<{
    [id: string]: boolean | number[];
  }>({});

  const getPoLineItem = useCallback(() => {
    const actionData = modifyRequest(dataState);
    const LineItemPrams = { id: poId, postBody: actionData };
    return dispatch(fetchPOLineItemsData(LineItemPrams));
  }, [dataState, dispatch, poId]);

  useEffect(() => {
    if (!poId) return;
    setLoading(true);
    getPoLineItem()
      .unwrap()
      .then((res) => {
        const data = res.items.map((item: IPoLineItems[]) => ({ ...item, rowId: uuid() }));
        setLineItemData(data);
        setCount(res.totalRecords);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [getPoLineItem, poId]);

  const onLineDataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };

  const onSelectionChange = React.useCallback(
    (event: GridSelectionChangeEvent) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedRow,
        dataItemKey: DATA_ITEM_KEY,
      });
      setSelectedRow(newSelectedState);
      setLineItemData((prev) => {
        const selectedLineItemData = prev.map((dataItem) => ({
          ...dataItem,
          selected: newSelectedState[ItemIdGetter(dataItem)],
        }));
        return selectedLineItemData;
      });
    },
    [selectedRow]
  );

  // clear filter
  const handleClearFilter = useCallback(() => {
    setDataState((prev) => ({
      ...GridDataState,
      skip: prev.skip,
      take: prev.take,
    }));
  }, []);

  const onRowDoubleClick = (event: GridRowDoubleClickEvent) => {
    const key = event.dataItem[DATA_ITEM_KEY];
    setLineItemData((prev) => {
      const lineItems = [...prev].map((item) => {
        if (item.rowId === key) {
          return { ...item, selected: false };
        } else return item;
      });
      return lineItems;
    });
  };
  const isClearFiltersDisabled = useMemo(
    () => !dataState.filter?.filters.length,
    [dataState.filter?.filters.length]
  );

  return (
    <>
      <p className="font-bold text-sm mt-2 mb-2">{title}</p>
      <GridWrapper
        id="poLineItemGrid"
        columns={poLineItemColumns}
        dataSource={lineItemData}
        dataState={dataState}
        height={'75vh'}
        total={count}
        onDataStateChange={onLineDataStateChange}
        {...defaultProps}
        gridToolbarChildren={
          <InvoiceGridToolbar
            clearFilter={handleClearFilter}
            isClearFiltersDisabled={isClearFiltersDisabled}
          />
        }
        onSelectionChange={onSelectionChange}
        loading={loading}
        onRowDoubleClick={onRowDoubleClick}
      />
    </>
  );
};
export default LineItemWizard;
